
import { useState, useMemo } from "react";
import ContentLibraryHeader from "./ContentLibraryHeader";
import ContentLibraryFilters from "./ContentLibraryFilters";
import ContentCard from "./ContentCard";
import { contentLibraryData } from "@/data/contentLibraryData";
import { ContentFilterState } from "@/types/content-library";
import { useDbContentFavorites } from "@/hooks/useDbContentFavorites";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const ContentLibrary = () => {
  const [filters, setFilters] = useState<ContentFilterState>({
    searchQuery: "",
    selectedCategory: "all",
  });
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const { favorites, isLoading: favoritesLoading } = useDbContentFavorites();
  
  const filteredContent = useMemo(() => {
    return contentLibraryData.filter((item) => {
      // Filter by favorites if selected
      if (showOnlyFavorites && !favorites.includes(item.id)) {
        return false;
      }
      
      // Filter by category
      if (filters.selectedCategory !== "all" && item.category !== filters.selectedCategory) {
        return false;
      }
      
      // Filter by search query
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        return (
          item.title.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower) ||
          item.category.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  }, [filters, favorites, showOnlyFavorites]);

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-hidden px-2 sm:px-4 lg:px-6">
      <ContentLibraryHeader />
      
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div className="w-full lg:flex-1 lg:max-w-2xl">
          <ContentLibraryFilters 
            filters={filters}
            onChange={setFilters}
          />
        </div>
        
        <div className="flex items-center space-x-2 lg:flex-shrink-0">
          <Switch 
            id="favorites-filter" 
            checked={showOnlyFavorites} 
            onCheckedChange={setShowOnlyFavorites} 
            disabled={favoritesLoading}
          />
          <Label htmlFor="favorites-filter" className="cursor-pointer text-sm whitespace-nowrap">
            Mostrar apenas favoritos
          </Label>
        </div>
      </div>
      
      {filteredContent.length === 0 ? (
        <div className="text-center py-8 sm:py-12 border rounded-lg bg-gray-50 mx-auto max-w-2xl">
          <h3 className="text-lg font-medium">Nenhum conteúdo encontrado</h3>
          <p className="text-muted-foreground mt-2 text-sm px-4">
            {showOnlyFavorites 
              ? "Você ainda não possui conteúdos favoritos. Adicione alguns conteúdos aos favoritos para vê-los aqui."
              : "Tente ajustar os filtros ou a busca para encontrar o conteúdo desejado."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {filteredContent.map((content) => (
            <ContentCard key={content.id} content={content} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentLibrary;
